// Pure pivoting for the adset-initiative cube window (the `initiatives`
// source). The adset media cube is queried grouped by the `initiative_type`
// dimension, which yields MULTIPLE rows per date bucket — but queryTheCube's
// YoY merge and processMetrics both require exactly one row per date. This
// pivot runs on each raw compareDateRange window (current + comparison)
// BEFORE the merge, collapsing the per-type rows into one wide row per date
// keyed by the synthetic `initiative.<TYPE>.<field>` and
// `initiative_category.<CATEGORY>.<field>` metric keys. No cube/React deps so
// it is unit-testable; connect.ts supplies the category resolver (which knows
// the company's custom initiative types).
import {
  INITIATIVE_METRIC_FIELDS,
  initiativeCategoryMetricKey,
  initiativeTypeMetricKey,
  UNASSIGNED_INITIATIVE_TYPE,
  type InitiativeCategoryValue,
} from "~/shared/kpi"

export const ADSET_INITIATIVE_TYPE_DIMENSION =
  "company_media_metrics_adsets.initiative_type"

/** Cube measures the initiative pivot reads (order mirrors the field list). */
export const ADSET_INITIATIVE_MEASURES = INITIATIVE_METRIC_FIELDS.map(
  field => field.cubeMeasure,
)

// Raw compareDateRange loadResponse values can be driver strings — the
// compare path never numeric-parses (only the non-compare fallback does), so
// coerce here before summing.
function toNumber(value: unknown): number {
  const n = typeof value === "number" ? value : parseFloat(value as string)
  return Number.isFinite(n) ? n : 0
}

/**
 * Pivots one raw cube window (rows per date × initiative_type) into one wide
 * row per date. Ad sets with a NULL/empty initiative_type land in the
 * UNASSIGNED type bucket and are excluded from both category rollups, so
 * Demand Gen + Demand Capture + Unassigned reconciles to the channel total.
 * The date value is kept under both `dateKey` (the granularity-suffixed key
 * resolveDateKey found on the raw rows) and the base `md_date` key, mirroring
 * un-pivoted cube rows.
 */
export function pivotInitiativeWindow(
  rows: Array<Record<string, unknown>>,
  dateKey: string,
  baseDateKey: string,
  categoryForType: (typeValue: string) => InitiativeCategoryValue,
): Array<Record<string, unknown>> {
  const byDate = new Map<string, Record<string, unknown>>()

  for (const row of rows) {
    const date = row[dateKey]
    if (date === null || date === undefined || date === "") continue
    const dateStr = String(date)

    let pivoted = byDate.get(dateStr)
    if (!pivoted) {
      pivoted = { [dateKey]: date, [baseDateKey]: row[baseDateKey] ?? date }
      byDate.set(dateStr, pivoted)
    }

    const rawType = row[ADSET_INITIATIVE_TYPE_DIMENSION]
    const typeValue =
      typeof rawType === "string" && rawType !== ""
        ? rawType
        : UNASSIGNED_INITIATIVE_TYPE

    for (const field of INITIATIVE_METRIC_FIELDS) {
      const value = toNumber(row[field.cubeMeasure])
      const typeKey = initiativeTypeMetricKey(typeValue, field.field)
      pivoted[typeKey] = toNumber(pivoted[typeKey]) + value
      if (typeValue !== UNASSIGNED_INITIATIVE_TYPE) {
        const categoryKey = initiativeCategoryMetricKey(
          categoryForType(typeValue),
          field.field,
        )
        pivoted[categoryKey] = toNumber(pivoted[categoryKey]) + value
      }
    }
  }

  return [...byDate.values()].sort(
    (a, b) =>
      new Date(a[dateKey] as string).getTime() -
      new Date(b[dateKey] as string).getTime(),
  )
}
